import { i as isMobile } from "./utilities-Da0sjanJ.js";
class SidebarComponent extends HTMLElement {
  connectedCallback() {
    if (this.dataset.position !== "true") return;
    this._sidebar = this.querySelector(".sidebar");
    this._sentinel = this.querySelector(".sidebar-sentinel");
    if (!this._sidebar || !this._sentinel) return;
    this._header = document.querySelector(".header-v3-wrap") || document.querySelector("header-v3-component");
    this._initStickyObserver();
    this._initHeaderObserver();
    this._onResize = () => {
      if (this._resizeRAF) return;
      this._resizeRAF = requestAnimationFrame(() => {
        if (isMobile()) {
          this._destroyStickyObserver();
          this._initStickyObserver();
          this._destroyHeaderObserver();
          this._initHeaderObserver();
        }
        this._resizeRAF = null;
      });
    };
    window.addEventListener("resize", this._onResize, { passive: true });
  }
  /* ── Sticky (IntersectionObserver) ── */
  _initStickyObserver() {
    var _a, _b;
    if (!isMobile()) {
      this._sidebar.classList.remove("is-sticky");
      document.body.style.setProperty("--sidebar-article-height", "0px");
      return;
    }
    const hasToc = !!this.querySelector(".toc");
    this._sidebar.classList.toggle("no-toc", !hasToc);
    if (!hasToc) return;
    let rootMargin = "0px";
    if (((_a = this._header) == null ? void 0 : _a.dataset.sticky) === "true" && ((_b = this._header) == null ? void 0 : _b.dataset.stickyMode) === "always") {
      rootMargin = `-${this._header.getBoundingClientRect().height}px 0px 0px 0px`;
    }
    this._stickyObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const isSticky = !entry.isIntersecting;
          this._sidebar.classList.toggle("is-sticky", isSticky);
          this._updateSidebarHeight(isSticky);
        }
      },
      { threshold: 0, rootMargin }
    );
    this._stickyObserver.observe(this._sentinel);
  }
  _destroyStickyObserver() {
    if (!this._stickyObserver) return;
    this._stickyObserver.disconnect();
    this._stickyObserver = null;
  }
  /* ── Sidebar height for layout offset ── */
  _updateSidebarHeight(isSticky) {
    if (isSticky) {
      const height = this._sidebar.getBoundingClientRect().height;
      document.body.style.setProperty("--sidebar-article-height", `${height}px`);
      if (!this._hasScrollCorrected && window.location.hash) {
        this._hasScrollCorrected = true;
        const target = document.querySelector(window.location.hash);
        if (target) {
          requestAnimationFrame(() => {
            target.scrollIntoView({ behavior: "smooth", block: "start" });
          });
        }
      }
    } else {
      document.body.style.setProperty("--sidebar-article-height", "0px");
    }
  }
  /* ── Header sticky class ──
     Replaces `body:has(.header-v3-wrap.is-sticky:not(.is-hidden))` with a
     MutationObserver to avoid :has() style-invalidation jank during the
     sidebar sticky transition. Toggles `.header-is-sticky` on .sidebar
     so CSS can react without scanning the entire body subtree. */
  _initHeaderObserver() {
    if (!isMobile() || !this._header) return;
    this._headerObserver = new MutationObserver(() => {
      this._headerStickyClass();
    });
    this._headerObserver.observe(this._header, {
      attributes: true,
      attributeFilter: ["class"]
    });
    this._headerStickyClass();
  }
  _headerStickyClass() {
    const isSticky = this._header.classList.contains("is-sticky") && !this._header.classList.contains("is-hidden");
    this._sidebar.classList.toggle("header-is-sticky", isSticky);
  }
  _destroyHeaderObserver() {
    if (!this._headerObserver) return;
    this._headerObserver.disconnect();
    this._headerObserver = null;
  }
  /* ── Cleanup ── */
  disconnectedCallback() {
    this._destroyStickyObserver();
    this._destroyHeaderObserver();
    window.removeEventListener("resize", this._onResize);
    if (this._resizeRAF) cancelAnimationFrame(this._resizeRAF);
  }
}
if (!customElements.get("sidebar-component")) {
  customElements.define("sidebar-component", SidebarComponent);
}
